import { PDFDocument } from 'pdf-lib'
import JSZip from 'jszip'

export class PdfSplitApp {
  constructor() {
    this.container = null;
    this.selectedFile = null;
    this.pdfDoc = null;
    this.totalPages = 0;
    this.settings = this.getDefaultSettings();
  }

  getDefaultSettings() {
    return {
      // Main settings (visible by default)
      quality: 'high',
      preserveMetadata: true,
      pageRange: '', // empty means all pages
      namingPattern: 'page-{number}',
      compression: true,
      zipPages: true, // ZIP all pages into single download
      
      // Advanced settings (hidden by default)
      zeroPadding: 0, // for {number:3} patterns
      
      // Metadata preservation options (advanced)
      preserveTitle: true,
      preserveAuthor: true,
      preserveSubject: true
    };
  }

  init(container) {
    this.container = container;
    this.render();
    this.setupEventListeners();
  }

  render() {
    this.container.innerHTML = `
      <div class="container">
        <div class="pdfsplit-app">
          <!-- File Upload Section -->
          <div class="upload-section">
            <div class="upload-area" id="upload-area">
              <div class="upload-icon">📄</div>
              <h3>Select PDF to Split</h3>
              <p>Choose a PDF file to extract pages from</p>
              <button class="upload-button" id="file-select-btn">
                <span>📎 Choose PDF File</span>
              </button>
              <input type="file" id="file-input" accept=".pdf,application/pdf" style="display: none;">
              <div class="upload-hint">Or drag and drop a PDF here</div>
            </div>
            
            <div class="selected-file" id="selected-file" style="display: none;">
              <h4>Selected PDF:</h4>
              <div class="file-info" id="file-info"></div>
              <button class="clear-file" id="clear-file">🗑️ Clear File</button>
            </div>
          </div>

          <!-- Settings Section -->
          <div class="settings-section">
            <h3>Split Settings</h3>
            
            <!-- Main Settings -->
            <div class="settings-group main-settings">
              <div class="setting-row">
                <label for="page-range">Page Range:</label>
                <input 
                  type="text" 
                  id="page-range" 
                  class="setting-input" 
                  placeholder="e.g., 1-5 or 2,4,6-8 (leave empty for all pages)"
                  value=""
                >
                <small class="setting-hint">Examples: "1-5", "2,4,6-8", "1,3,5-10"</small>
              </div>

              <div class="setting-row">
                <label for="naming-pattern">File Naming Pattern:</label>
                <select id="naming-pattern" class="setting-input">
                  <option value="page-{number}">page-1, page-2, page-3...</option>
                  <option value="{original}-page-{number}">[original-name]-page-1, [original-name]-page-2...</option>
                  <option value="page-{number:3}">page-001, page-002, page-003...</option>
                  <option value="{original}-{number:3}">[original-name]-001, [original-name]-002...</option>
                  <option value="custom">Custom pattern</option>
                </select>
              </div>

              <div class="setting-row custom-pattern" id="custom-pattern" style="display: none;">
                <label for="custom-naming">Custom Pattern:</label>
                <input 
                  type="text" 
                  id="custom-naming" 
                  class="setting-input" 
                  placeholder="e.g., doc-{number} or {original}-split-{number:4}"
                >
                <small class="setting-hint">Use {original} for original filename, {number} for page number, {number:X} for zero-padded numbers</small>
              </div>

              <div class="setting-row">
                <label for="quality">Quality:</label>
                <select id="quality" class="setting-input">
                  <option value="high">High (best quality, larger files)</option>
                  <option value="medium">Medium (balanced)</option>
                  <option value="low">Low (smallest files)</option>
                </select>
              </div>

              <div class="setting-row">
                <label>
                  <input type="checkbox" id="preserve-metadata" class="setting-checkbox" checked>
                  Preserve PDF metadata in split pages
                </label>
              </div>

              <div class="setting-row">
                <label>
                  <input type="checkbox" id="compression" class="setting-checkbox" checked>
                  Enable compression
                </label>
              </div>

              <div class="setting-row zip-pages-setting">
                <label>
                  <input type="checkbox" id="zip-pages" class="setting-checkbox" checked>
                  ZIP all pages into single download
                </label>
                <small class="setting-hint">When enabled, downloads one ZIP file instead of multiple PDFs</small>
              </div>
            </div>

            <!-- Advanced Settings Toggle -->
            <button class="advanced-toggle" id="advanced-toggle">
              <span>⚙️ Advanced Settings</span>
              <span class="toggle-icon">▼</span>
            </button>

            <!-- Advanced Settings (Hidden by default) -->
            <div class="settings-group advanced-settings" id="advanced-settings" style="display: none;">
              <div class="metadata-options">
                <h4>Metadata Preservation Options</h4>
                
                <div class="checkbox-group">
                  <label>
                    <input type="checkbox" id="preserve-title" class="setting-checkbox" checked>
                    Preserve document title
                  </label>
                </div>
                
                <div class="checkbox-group">
                  <label>
                    <input type="checkbox" id="preserve-author" class="setting-checkbox" checked>
                    Preserve author information
                  </label>
                </div>
                
                <div class="checkbox-group">
                  <label>
                    <input type="checkbox" id="preserve-subject" class="setting-checkbox" checked>
                    Preserve subject/description
                  </label>
                </div>
              </div>

              <div class="setting-row">
                <label for="zero-padding">Zero Padding for Numbers:</label>
                <input type="number" id="zero-padding" class="setting-input" min="0" max="6" value="0">
                <small class="setting-hint">0 = no padding, 3 = 001, 002, 003...</small>
              </div>
            </div>
          </div>

          <!-- Preview Section -->
          <div class="preview-section" id="preview-section" style="display: none;">
            <h3>Split Preview</h3>
            <div class="preview-info" id="preview-info"></div>
            <div class="preview-samples" id="preview-samples"></div>
          </div>

          <!-- Split Section -->
          <div class="split-section">
            <button class="split-button" id="split-button" disabled>
              <span class="split-icon">✂️</span>
              <span class="split-text">Split PDF</span>
            </button>
            
            <div class="progress-section" id="progress-section" style="display: none;">
              <div class="progress-bar">
                <div class="progress-fill" id="progress-fill"></div>
              </div>
              <div class="progress-text" id="progress-text">Splitting...</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
  setupEventListeners() {
    const fileInput = document.getElementById('file-input');
    const fileSelectBtn = document.getElementById('file-select-btn');
    const uploadArea = document.getElementById('upload-area');
    const clearFileBtn = document.getElementById('clear-file');
    const splitBtn = document.getElementById('split-button');
    const advancedToggle = document.getElementById('advanced-toggle');

    // File selection
    fileSelectBtn.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', (e) => this.handleFileSelection(e.target.files[0]));

    // Drag and drop
    uploadArea.addEventListener('dragover', this.handleDragOver.bind(this));
    uploadArea.addEventListener('drop', this.handleFileDrop.bind(this));
    uploadArea.addEventListener('dragleave', this.handleDragLeave.bind(this));

    // Clear file
    clearFileBtn.addEventListener('click', () => this.clearFile());

    // Split button
    splitBtn.addEventListener('click', () => this.splitPDF());

    // Advanced settings toggle
    advancedToggle.addEventListener('click', () => this.toggleAdvancedSettings());

    // Settings change listeners
    this.setupSettingsListeners();
  }

  setupSettingsListeners() {
    // Page range input
    document.getElementById('page-range').addEventListener('input', (e) => {
      this.settings.pageRange = e.target.value.trim();
      this.updatePreview();
    });

    // Naming pattern
    document.getElementById('naming-pattern').addEventListener('change', (e) => {
      this.settings.namingPattern = e.target.value;
      
      // Show/hide custom pattern input
      const customPattern = document.getElementById('custom-pattern');
      if (e.target.value === 'custom') {
        customPattern.style.display = 'block';
        this.settings.namingPattern = document.getElementById('custom-naming').value || 'page-{number}';
      } else {
        customPattern.style.display = 'none';
      }
      
      this.updatePreview();
    });

    // Custom naming pattern
    document.getElementById('custom-naming').addEventListener('input', (e) => {
      if (document.getElementById('naming-pattern').value === 'custom') {
        this.settings.namingPattern = e.target.value || 'page-{number}';
        this.updatePreview();
      }
    });

    // Quality
    document.getElementById('quality').addEventListener('change', (e) => {
      this.settings.quality = e.target.value;
    });

    // Preserve metadata
    document.getElementById('preserve-metadata').addEventListener('change', (e) => {
      this.settings.preserveMetadata = e.target.checked;
    });

    // Compression
    document.getElementById('compression').addEventListener('change', (e) => {
      this.settings.compression = e.target.checked;
    });

    // ZIP pages
    document.getElementById('zip-pages').addEventListener('change', (e) => {
      this.settings.zipPages = e.target.checked;
      this.updatePreview();
    });

    // Advanced metadata options
    ['preserve-title', 'preserve-author', 'preserve-subject'].forEach(id => {
      document.getElementById(id).addEventListener('change', (e) => {
        const property = id.replace('-', '').charAt(0).toUpperCase() + id.replace('-', '').slice(1);
        this.settings[property] = e.target.checked;
      });
    });

    // Zero padding
    document.getElementById('zero-padding').addEventListener('change', (e) => {
      this.settings.zeroPadding = parseInt(e.target.value) || 0;
      this.updatePreview();
    });
  }

  async handleFileSelection(file) {
    if (!file) return;

    if (file.type !== 'application/pdf') {
      this.showError('Please select a valid PDF file');
      return;
    }

    try {
      this.showProgress('Loading PDF...');
      
      const arrayBuffer = await file.arrayBuffer();
      this.pdfDoc = await PDFDocument.load(arrayBuffer);
      this.totalPages = this.pdfDoc.getPageCount();
      this.selectedFile = file;

      this.updateFileInfo();
      this.updateSplitButton();
      this.updatePreview();
      
      this.hideProgress();
      this.showSuccess(`PDF loaded successfully! ${this.totalPages} pages found.`);

    } catch (error) {
      console.error('Error loading PDF:', error);
      this.hideProgress();
      this.showError(`Failed to load PDF: ${error.message}`);
      this.clearFile();
    }
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('drag-over');
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');
  }

  handleFileDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  updateFileInfo() {
    const fileInfoContainer = document.getElementById('selected-file');
    const fileInfo = document.getElementById('file-info');

    if (!this.selectedFile) {
      fileInfoContainer.style.display = 'none';
      return;
    }

    fileInfoContainer.style.display = 'block';
    fileInfo.innerHTML = `
      <div class="file-details">
        <div class="file-name">${this.selectedFile.name}</div>
        <div class="file-meta">
          <span class="file-size">${this.formatFileSize(this.selectedFile.size)}</span>
          <span class="page-count">${this.totalPages} pages</span>
        </div>
      </div>
    `;
  }
  clearFile() {
    this.selectedFile = null;
    this.pdfDoc = null;
    this.totalPages = 0;
    this.updateFileInfo();
    this.updateSplitButton();
    this.updatePreview();
    
    // Clear file input
    document.getElementById('file-input').value = '';
  }

  updateSplitButton() {
    const splitBtn = document.getElementById('split-button');
    splitBtn.disabled = !this.selectedFile;
  }

  updatePreview() {
    const previewSection = document.getElementById('preview-section');
    const previewInfo = document.getElementById('preview-info');
    const previewSamples = document.getElementById('preview-samples');

    if (!this.selectedFile) {
      previewSection.style.display = 'none';
      return;
    }

    previewSection.style.display = 'block';

    try {
      const pageNumbers = this.getPageNumbers(this.totalPages);
      const originalName = this.selectedFile.name.replace('.pdf', '');
      
      previewInfo.innerHTML = `
        <div class="preview-stats">
          <div class="stat">
            <strong>Pages to extract:</strong> ${pageNumbers.length} of ${this.totalPages}
          </div>
          <div class="stat">
            <strong>Page range:</strong> ${this.settings.pageRange || 'All pages'}
          </div>
          <div class="stat">
            <strong>Download format:</strong> ${this.settings.zipPages && pageNumbers.length > 1 ? 'Single ZIP file' : 'Individual PDF files'}
          </div>
        </div>
      `;

      // Show sample filenames
      const sampleCount = Math.min(3, pageNumbers.length);
      const samples = pageNumbers.slice(0, sampleCount).map(pageNum => 
        this.generateFileName(originalName, pageNum, this.totalPages)
      );

      let samplesHtml = '<div class="filename-samples"><strong>Sample filenames:</strong><ul>';
      samples.forEach(filename => {
        samplesHtml += `<li><code>${filename}</code></li>`;
      });
      
      if (pageNumbers.length > sampleCount) {
        samplesHtml += `<li><em>... and ${pageNumbers.length - sampleCount} more</em></li>`;
      }
      
      samplesHtml += '</ul></div>';
      previewSamples.innerHTML = samplesHtml;

    } catch (error) {
      previewInfo.innerHTML = `<div class="error">Invalid page range: ${error.message}</div>`;
      previewSamples.innerHTML = '';
    }
  }

  toggleAdvancedSettings() {
    const advancedSettings = document.getElementById('advanced-settings');
    const toggleIcon = document.querySelector('.toggle-icon');
    
    if (advancedSettings.style.display === 'none') {
      advancedSettings.style.display = 'block';
      toggleIcon.textContent = '▲';
    } else {
      advancedSettings.style.display = 'none';
      toggleIcon.textContent = '▼';
    }
  }

  async splitPDF() {
    if (!this.selectedFile || !this.pdfDoc) return;

    try {
      this.showProgress('Analyzing pages...');
      
      const pageNumbers = this.getPageNumbers(this.totalPages);
      const originalName = this.selectedFile.name.replace('.pdf', '');
      
      this.updateProgress(`Splitting ${pageNumbers.length} pages...`, 0.1);

      const results = [];
      
      for (let i = 0; i < pageNumbers.length; i++) {
        const pageNumber = pageNumbers[i];
        
        this.updateProgress(
          `Processing page ${pageNumber} (${i + 1} of ${pageNumbers.length})...`,
          0.1 + (i / pageNumbers.length) * 0.8
        );

        try {
          const result = await this.extractPage(this.pdfDoc, pageNumber, originalName);
          results.push(result);
        } catch (error) {
          console.error(`Error extracting page ${pageNumber}:`, error);
          results.push({
            pageNumber,
            success: false,
            error: error.message
          });
        }
      }

      this.updateProgress('Preparing downloads...', 0.95);

      // Successful extractions
      const successful = results.filter(r => r.success);
      
      if (successful.length === 0) {
        throw new Error('No pages were successfully extracted');
      }

      // Download files based on ZIP setting
      if (this.settings.zipPages && successful.length > 1) {
        // Create ZIP file with all pages
        await this.downloadAsZip(successful, originalName);
      } else {
        // Download individual files
        for (const result of successful) {
          await this.downloadFile(result.pdfBytes, result.fileName);
          // Small delay between downloads to avoid browser blocking
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      this.updateProgress('Complete!', 1.0);
      this.hideProgress();
      
      const failedCount = results.length - successful.length;
      let message;
      
      if (this.settings.zipPages && successful.length > 1) {
        message = `Successfully split ${successful.length} pages into ZIP file!`;
      } else {
        message = `Successfully split ${successful.length} pages!`;
      }
      
      if (failedCount > 0) {
        message += ` (${failedCount} pages failed)`;
      }
      
      this.showSuccess(message);

    } catch (error) {
      console.error('Error splitting PDF:', error);
      this.hideProgress();
      this.showError(`Split failed: ${error.message}`);
    }
  }

  async extractPage(sourcePDF, pageNumber, originalName) {
    // Create a new PDF document for this page
    const newPdfDoc = await PDFDocument.create();
    
    // Copy metadata if requested
    if (this.settings.preserveMetadata) {
      if (this.settings.preserveTitle) {
        const title = sourcePDF.getTitle();
        if (title) newPdfDoc.setTitle(`${title} - Page ${pageNumber}`);
      }
      
      if (this.settings.preserveAuthor) {
        const author = sourcePDF.getAuthor();
        if (author) newPdfDoc.setAuthor(author);
      }
      
      if (this.settings.preserveSubject) {
        const subject = sourcePDF.getSubject();
        if (subject) newPdfDoc.setSubject(subject);
      }
      
      newPdfDoc.setCreator('PDF Splitter Web App');
      newPdfDoc.setProducer('PDF Splitter Web App');
      newPdfDoc.setCreationDate(new Date());
    }

    // Copy the specific page (PDF pages are 0-indexed)
    const [copiedPage] = await newPdfDoc.copyPages(sourcePDF, [pageNumber - 1]);
    newPdfDoc.addPage(copiedPage);

    // Generate filename
    const fileName = this.generateFileName(originalName, pageNumber, this.totalPages);

    // Apply quality settings
    const saveOptions = this.getSaveOptions();
    
    // Save the page as PDF bytes
    const pdfBytes = await newPdfDoc.save(saveOptions);

    return {
      success: true,
      pageNumber,
      fileName,
      pdfBytes,
      fileSize: pdfBytes.length
    };
  }
  generateFileName(originalName, pageNumber, totalPages) {
    let pattern = this.settings.namingPattern;
    
    // Handle zero-padding patterns like {number:3}
    const paddingMatch = pattern.match(/\{number:(\d+)\}/);
    if (paddingMatch) {
      const paddingLength = parseInt(paddingMatch[1]);
      const paddedNumber = pageNumber.toString().padStart(paddingLength, '0');
      pattern = pattern.replace(/\{number:\d+\}/, paddedNumber);
    } else {
      // Apply zero padding from settings if no specific padding in pattern
      const padding = this.settings.zeroPadding || 0;
      const paddedNumber = padding > 0 ? 
        pageNumber.toString().padStart(padding, '0') : 
        pageNumber.toString();
      pattern = pattern.replace('{number}', paddedNumber);
    }
    
    pattern = pattern.replace('{original}', originalName);
    
    return `${pattern}.pdf`;
  }

  getPageNumbers(totalPages) {
    if (!this.settings.pageRange) {
      // Return all pages
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = new Set();
    const ranges = this.settings.pageRange.split(',');

    for (const range of ranges) {
      const trimmedRange = range.trim();
      
      if (trimmedRange.includes('-')) {
        // Handle range like "1-5"
        const [start, end] = trimmedRange.split('-').map(n => parseInt(n.trim()));
        if (isNaN(start) || isNaN(end)) {
          throw new Error(`Invalid page range: ${trimmedRange}`);
        }
        
        if (start > end) {
          throw new Error(`Invalid range: start page (${start}) is greater than end page (${end})`);
        }
        
        for (let i = Math.max(1, start); i <= Math.min(totalPages, end); i++) {
          pages.add(i);
        }
      } else {
        // Handle single page like "3"
        const pageNum = parseInt(trimmedRange);
        if (isNaN(pageNum)) {
          throw new Error(`Invalid page number: ${trimmedRange}`);
        }
        
        if (pageNum < 1 || pageNum > totalPages) {
          throw new Error(`Page ${pageNum} is out of range (1-${totalPages})`);
        }
        
        pages.add(pageNum);
      }
    }

    if (pages.size === 0) {
      throw new Error('No valid pages found in range');
    }

    return Array.from(pages).sort((a, b) => a - b);
  }

  getSaveOptions() {
    const baseOptions = {
      useObjectStreams: this.settings.compression,
      addDefaultPage: false
    };

    // Quality-based compression settings
    switch (this.settings.quality.toLowerCase()) {
      case 'low':
        return {
          ...baseOptions,
          objectsPerTick: 50,
          updateFieldAppearances: false
        };
      case 'medium':
        return {
          ...baseOptions,
          objectsPerTick: 100,
          updateFieldAppearances: true
        };
      case 'high':
      default:
        return {
          ...baseOptions,
          objectsPerTick: 200,
          updateFieldAppearances: true
        };
    }
  }

  async downloadAsZip(results, originalName) {
    this.updateProgress('Creating ZIP file...', 0.96);
    
    const zip = new JSZip();
    
    // Add each PDF to the ZIP
    for (const result of results) {
      zip.file(result.fileName, result.pdfBytes);
    }
    
    this.updateProgress('Generating ZIP...', 0.98);
    
    // Generate ZIP blob
    const zipBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6
      }
    });
    
    // Download the ZIP file
    const zipFileName = `${originalName}-split-pages.zip`;
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = zipFileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async downloadFile(data, filename) {
    const blob = new Blob([data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  showProgress(message) {
    const progressSection = document.getElementById('progress-section');
    const splitButton = document.getElementById('split-button');
    
    progressSection.style.display = 'block';
    splitButton.disabled = true;
    
    this.updateProgress(message, 0);
  }

  updateProgress(message, progress) {
    const progressText = document.getElementById('progress-text');
    const progressFill = document.getElementById('progress-fill');
    
    if (progressText) progressText.textContent = message;
    if (progressFill) progressFill.style.width = `${Math.round(progress * 100)}%`;
  }

  hideProgress() {
    const progressSection = document.getElementById('progress-section');
    const splitButton = document.getElementById('split-button');
    
    setTimeout(() => {
      if (progressSection) progressSection.style.display = 'none';
      if (splitButton) splitButton.disabled = !this.selectedFile;
    }, 2000);
  }

  showSuccess(message) {
    this.showNotification(message, 'success');
  }

  showError(message) {
    this.showNotification(message, 'error');
  }

  showNotification(message, type) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    
    // Add to container
    this.container.appendChild(notification);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 5000);
  }

  destroy() {
    // Clean up any resources if needed
    this.selectedFile = null;
    this.pdfDoc = null;
    this.totalPages = 0;
    if (this.container) {
      this.container.innerHTML = '';
    }
  }
}